// src/components/App.tsx
import React, { useState, useEffect, Suspense, createContext } from "react";
import { lazy } from 'react';
import { useMsal } from "@azure/msal-react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  CircularProgress,
  Card,
  CardContent,
  Box,
  Alert,
  AlertTitle
} from "@mui/material";
import "./App.css";
import LoginIcon from "@mui/icons-material/Login";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MainLayout from "./MainLayout";
import Footer from "./Footer";
import AccessDenied from "./AccessDenied";
import TenantManager from "./TenantManager"; 
import { 
  BrowserRouter as Router, 
  Route, 
  Routes,
  useNavigate,
  useLocation,
  Navigate,
  createRoutesFromElements,
  Link
} from 'react-router-dom';
import "@fontsource/montserrat/700.css";
import LandingPage from "./LandingPage";
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';

// Import TokenService for consistent token management
import { TokenService } from '../utils/tokenService';
import { InteractionRequiredAuthError, AccountInfo } from "@azure/msal-browser";
import PopupBlockerDetector from "./PopupBlockerDetector";
import { clientId } from "../authConfig";

// NEW: Import the AppCacheProvider
import { AppCacheProvider } from '../context/AppCacheContext';

// Inactivity auto-sign-out
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import IdleWarningDialog from './IdleWarningDialog';

const IDLE_TOTAL_MS = 15 * 60 * 1000;   // 15 minutes total inactivity before auto sign-out
const IDLE_WARNING_MS = 120 * 1000;     // 120-second warning before sign-out
const LOGIN_HINT_KEY = 'apptesting.lastLoginHint';

// Resolve the value to pass as `logoutHint` so Entra ID signs out the specific
// account without showing the account-selection screen. Prefers the
// `login_hint` optional claim (when configured on the ID token) and falls back
// to the account UPN.
const getLogoutHint = (account?: AccountInfo): string | undefined => {
  const claims = (account?.idTokenClaims as Record<string, unknown> | undefined) || {};
  return (typeof claims.login_hint === 'string' && claims.login_hint) || account?.username || undefined;
};

// Lazy load the MarketplaceLanding component
const MarketplaceLanding = lazy(() => import('./MarketplaceLanding'));

export const SubscriptionContext = createContext<{
  isSubscriptionActive: boolean;
  subscriptionData: any | null;
  subscriptionPlan: string;
  isLoading: boolean;
}>({
  isSubscriptionActive: false,
  subscriptionData: null,
  subscriptionPlan: '',
  isLoading: true,
});

// Create a wrapper component to provide routing context to MainLayout
// This eliminates the need for MainLayout to have its own Router
const MainLayoutWrapper: React.FC<{
  isMsalInitialized: boolean;
  userDisplayName: string;
}> = ({ isMsalInitialized, userDisplayName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pass navigate function and location to MainLayout via props
  return (
    <MainLayout 
      isMsalInitialized={isMsalInitialized} 
      userDisplayName={userDisplayName} 
      // Add these props to pass routing functionality
      navigate={navigate}
      location={location}
      // This prop tells MainLayout not to use its own Router
      routerProvided={true}
    />
  );
};

// NEW: Subscription Provider with App Cache Integration
const SubscriptionProviderWithCache: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscriptionData, setSubscriptionData] = useState<any | null>(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('');
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<boolean>(true);

  // Load subscription on mount
  useEffect(() => {
    const checkExistingSubscription = () => {
      try {
        const storedSubscription = localStorage.getItem('marketplaceSubscription');
        if (storedSubscription) {
          const parsedSubscription = JSON.parse(storedSubscription);
          setSubscriptionData(parsedSubscription);
          setSubscriptionPlan(parsedSubscription.planId || 'starter');
          setIsSubscriptionActive(true);
          console.log('📋 Loaded subscription:', parsedSubscription.planId);
        } else {
          // Default to starter plan if no subscription found
          setSubscriptionPlan('starter');
          console.log('📋 No subscription found, defaulting to starter plan');
        }
      } catch (error) {
        console.error("Error loading subscription data:", error);
        setSubscriptionPlan('starter'); // Fallback to starter
      } finally {
        setIsSubscriptionLoading(false);
      }
    };
    
    checkExistingSubscription();
  }, []);

  // Handler for successful subscription activation
  const handleSubscriptionComplete = (data: any) => {
    console.log("Subscription activated successfully:", data);
    setSubscriptionData(data);
    setSubscriptionPlan(data.planId || 'starter');
    setIsSubscriptionActive(true);
    setIsSubscriptionLoading(false);
    
    // Store in localStorage for persistence across sessions
    localStorage.setItem('marketplaceSubscription', JSON.stringify({
      subscriptionId: data.id,
      planId: data.planId,
      activatedOn: new Date().toISOString(),
      status: data.status || 'Active'
    }));
  };

  // Handler for subscription errors
  const handleSubscriptionError = (error: Error) => {
    console.error("Subscription activation error:", error);
    setIsSubscriptionLoading(false);
  };

  return (
    <SubscriptionContext.Provider value={{
      isSubscriptionActive,
      subscriptionData,
      subscriptionPlan,
      isLoading: isSubscriptionLoading,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

const App: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);
  const [tenantCheckComplete, setTenantCheckComplete] = useState(false);
  const [isTenantDisallowed, setIsTenantDisallowed] = useState(false);
  const [isTenantOnboarded, setIsTenantOnboarded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const userDisplayName = accounts.length > 0 ? accounts[0].name || accounts[0].username : null;
  const tenantId = accounts.length > 0 ? accounts[0].tenantId : null;

  const [allowedTenantIds, setAllowedTenantIds] = useState<string[]>([]);
  const [tenantConfigLoaded, setTenantConfigLoaded] = useState(false);
  const [configSource, setConfigSource] = useState<'api' | 'fallback' | 'none'>('none');

  // Idle auto-sign-out state
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);

  // Persist a login_hint so the next sign-in (after idle logout) pre-fills the
  // username field. Prefers the `login_hint` optional claim if it has been
  // added to the ID token; otherwise falls back to the account username (UPN).
  useEffect(() => {
    if (accounts.length > 0) {
      const acct = accounts[0];
      const claims = (acct.idTokenClaims as Record<string, unknown> | undefined) || {};
      const hint =
        (typeof claims.login_hint === 'string' && claims.login_hint) ||
        acct.username ||
        '';
      if (hint) {
        try {
          localStorage.setItem(LOGIN_HINT_KEY, hint);
        } catch {
          /* ignore quota / disabled storage */
        }
      }
    }
  }, [accounts]);

  // Load tenant configuration on app start
  useEffect(() => {
    const loadTenantConfig = async () => {
      try {
        // First: Try the dynamic API
        console.log('Attempting to load tenant config from API...');
        const apiResponse = await fetch('/api/get-allowed-tenants', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        if (apiResponse.ok) {
          const config = await apiResponse.json();
          setAllowedTenantIds(config.allowedTenantIds);
          setConfigSource('api');
          console.log('✅ Tenant config loaded from API:', config.allowedTenantIds.length, 'tenants');
          return;
        }
        
        // Enhanced error logging
        console.warn(`API failed with status: ${apiResponse.status} ${apiResponse.statusText}`);
        const errorText = await apiResponse.text();
        console.warn('API error response:', errorText.substring(0, 200));
        
        throw new Error(`API failed with status: ${apiResponse.status}`);
        
      } catch (apiError) {
        console.warn('❌ API failed, trying fallback file...', apiError);
        
        try {
          // Fallback: Load from static JSON file
          const fallbackResponse = await fetch('/config/allowed-tenants.json');
          
          if (fallbackResponse.ok) {
            const fallbackConfig = await fallbackResponse.json();
            setAllowedTenantIds(fallbackConfig.allowedTenantIds);
            setConfigSource('fallback');
            console.log('✅ Tenant config loaded from fallback file:', fallbackConfig.allowedTenantIds.length, 'tenants');
            return;
          }
          
          throw new Error(`Fallback file failed with status: ${fallbackResponse.status}`);
          
        } catch (fallbackError) {
          console.error('❌ Both API and fallback failed:', fallbackError);
          
          // Last resort: Empty array (blocks all tenants - fail safe)
          setAllowedTenantIds([]);
          setConfigSource('none');
          console.error('🚫 No tenant configuration available - blocking all access');
        }
      } finally {
        setTenantConfigLoaded(true);
      }
    };

    loadTenantConfig();
  }, []);

  const getTenantDisplayText = (tenantId: string): string => {
    return `Tenant ID: ${tenantId}`;
  };

  const handlePopupBlocked = (isBlocked: boolean) => {
    console.log("Popup blocking detected:", isBlocked);
  };

  // Initialize MSAL
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await instance.initialize();
        const response = await instance.handleRedirectPromise();
        if (response) {
          console.log("Redirect response:", response);
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
      } finally {
        setIsMsalInitialized(true);
      }
    };
    initializeMsal();
  }, [instance]);

  // Check if the tenant is allowed and onboarded
  useEffect(() => {
    if (!tenantCheckComplete && accounts.length > 0 && tenantConfigLoaded) {
      const tenantId = accounts[0].tenantId;
      console.log("Signed-in tenant ID:", tenantId);
      console.log("Config source:", configSource);

      // If no config available, block access
      if (configSource === 'none' || allowedTenantIds.length === 0) {
        console.log("🚫 No tenant configuration - access blocked");
        setIsTenantDisallowed(true);
        setTenantCheckComplete(true);
        return;
      }

      // Check if tenant is in allowlist
      if (!allowedTenantIds.includes(tenantId)) {
        console.log("🚫 Tenant not in allowlist - access blocked");
        setIsTenantDisallowed(true);
        setTenantCheckComplete(true);
        return;
      }

      console.log("✅ Tenant approved - proceeding with onboarding check");
      
      let isMounted = true;
      
      verifyTenantOnboarding(tenantId)
        .then(isOnboarded => {
          if (!isMounted) return;
          
          console.log("Tenant onboarding verification result:", isOnboarded);
          
          if (isOnboarded) {
            setIsTenantOnboarded(true);
            setShowOnboarding(false);
          } else {
            setShowOnboarding(true);
            setIsTenantOnboarded(false);
          }
          
          setTenantCheckComplete(true);
        })
        .catch(error => {
          if (!isMounted) return;
          
          console.error("Error verifying tenant onboarding:", error);
          setShowOnboarding(true);
          setIsTenantOnboarded(false);
          setTenantCheckComplete(true);
        });
        
      return () => {
        isMounted = false;
      };
    }
  }, [accounts, tenantCheckComplete, allowedTenantIds, tenantConfigLoaded, configSource, instance]);

  async function verifyTenantOnboarding(tenantId: string): Promise<boolean> {
    // For critical tenants, always return true without API check
    const criticalTenants = [
      "67b7de17-01a8-410a-a645-3eacd61c1111", 
      "ecca902a-0511-4b72-9fa9-e3b0cafc1e09"
    ];
    
    if (criticalTenants.includes(tenantId)) {
      console.log("Critical tenant detected, skipping API verification:", tenantId);
      return true;
    }

    // Add a small delay to ensure MSAL token cache is ready after page refresh
    if (performance.now() < 5000) { // If page loaded less than 5 seconds ago
      console.log("Page recently loaded, waiting for MSAL token cache to stabilize...");
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    try {
      console.log("Verifying tenant onboarding via Microsoft Graph API");
      
      // Enhanced retry logic for page refresh scenarios
      const maxRetries = 2;
      let lastError: any;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${2000 * attempt}ms delay`);
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          }
          
          // Try to acquire a token with the high-privilege scope
          const tokenPromise = instance.acquireTokenSilent({
            // scopes: ["https://graph.microsoft.com/Application.ReadWrite.All"],
            scopes: ["https://graph.microsoft.com/Application.Read.All"],
            account: accounts[0],
            forceRefresh: attempt > 0 // Force refresh on retry
          });
          
          // Increased timeout for page refresh scenarios
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error("Token acquisition timed out")), 15000);
          });
          
          // Race the token acquisition against the timeout
          const tokenResponse = await Promise.race([tokenPromise, timeoutPromise]);
          
          // Make a test call to verify permissions
          const response = await fetch('https://graph.microsoft.com/v1.0/applications?$top=1', {
            headers: {
              'Authorization': `Bearer ${tokenResponse.accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            console.log("Successfully verified Application.Read.All permission");
            return true;
          } else if (response.status === 403) {
            // Forbidden - permissions definitely not granted
            console.log("API call forbidden - tenant needs onboarding");
            return false;
          } else {
            // Other HTTP errors - might be temporary, continue to next attempt
            const errorText = await response.text();
            console.warn(`API call failed with status ${response.status}: ${errorText}`);
            lastError = new Error(`HTTP ${response.status}: ${errorText}`);
            continue;
          }
        } catch (error) {
          lastError = error;
          
          if (error instanceof InteractionRequiredAuthError || 
              (error instanceof Error && error.message.includes("consent_required"))) {
            // Definitive consent required - don't retry
            console.log("Consent required, tenant needs onboarding:", error);
            return false;
          }
          
          if (error instanceof Error && 
              error.message.includes("uninitialized_public_client_application")) {
            // MSAL not ready - continue retrying
            console.warn("MSAL not ready, will retry:", error);
            continue;
          }
          
          // For other errors, continue retrying
          console.warn(`Verification attempt ${attempt + 1} failed:`, error);
        }
      }
      
      // All attempts failed
      console.error("All verification attempts failed:", lastError);
      return false;
      
    } catch (error) {
      console.error("Unexpected error during tenant verification:", error);
      return false;
    }
  }    

  function clearTenantOnboardingStatus(tenantId: string): void {
    console.log(`Clearing onboarding status for tenant: ${tenantId}`);
  }

  const handleSubscriptionComplete = (data: any) => {
    console.log("Subscription activated successfully:", data);
    // Store in localStorage so SubscriptionProviderWithCache picks it up on next mount
    localStorage.setItem('marketplaceSubscription', JSON.stringify({
      subscriptionId: data.id,
      planId: data.planId,
      activatedOn: new Date().toISOString()
    }));
  };

  const handleSubscriptionError = (error: Error) => {
    console.error("Subscription activation error:", error);
  };

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    console.log("Onboarding complete for tenant:", tenantId);
    setIsTenantOnboarded(true);
    setShowOnboarding(false);
  };

  const handleCancelOnboarding = () => {
    // Reset the onboarding state
    setIsTenantDisallowed(false);
    setTenantCheckComplete(false);
    setShowOnboarding(false);
    
    // Sign out the current user
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    }).catch(error => {
      console.error("Error during logout:", error);
    });
  };

  // Sign-in / Sign-out
  const handleLogin = async () => {
    if (window.opener) {
      console.warn("Skipping loginPopup because we're already in a popup window.");
      return;
    }

    // Pre-fill the username field on re-auth using the last known login hint.
    let loginHint: string | undefined;
    try {
      loginHint = localStorage.getItem(LOGIN_HINT_KEY) || undefined;
    } catch {
      loginHint = undefined;
    }

    try {
      await instance.loginPopup({
        scopes: ["openid", "profile", "email"],
        loginHint,
      });
    } catch (error: any) {
      console.error("loginPopup failed:", error);
      instance.loginRedirect({
        scopes: ["openid", "profile", "email"],
        prompt: "consent",
        loginHint,
      });
    }
  };

  const handleLogout = () => {
    if (window.opener) {
      console.warn("Skipping logoutPopup because we're already in a popup window.");
      return;
    }

    // Explicit user sign-out: clear the saved hint so the next sign-in shows
    // the account picker rather than auto-filling the previous user.
    try { localStorage.removeItem(LOGIN_HINT_KEY); } catch { /* ignore */ }

    // Pass the account + logoutHint so Entra signs the user out directly instead
    // of prompting an account-selection screen during logout.
    const account = accounts[0];
    instance.logoutPopup({
      account: account ?? undefined,
      logoutHint: getLogoutHint(account),
    }).catch((error) => {
      console.error("Logout failed:", error);
    });
  };

  const handleTryAnotherAccount = async () => {
    try {
      await instance.logoutPopup();
    } catch (e) {
      console.error("Error logging out:", e);
    } finally {
      try { localStorage.removeItem(LOGIN_HINT_KEY); } catch { /* ignore */ }
      setIsTenantDisallowed(false);
      setTenantCheckComplete(false);
    }
  };

  // Idle auto-sign-out: warn 120s before, then sign out via MSAL.
  // Keeps the saved login_hint so the user can sign back in with one click.
  const handleIdleExpire = async () => {
    setIdleWarningOpen(false);
    const account = accounts[0];
    // Automated (non-user-initiated) sign-out must use a redirect: browsers block
    // popups that aren't triggered by a recent user gesture, so logoutPopup would
    // be silently blocked and leave the user signed in after the idle period.
    // logoutHint skips the account-selection screen on the way out.
    try {
      instance.logoutRedirect({
        account: account ?? undefined,
        logoutHint: getLogoutHint(account),
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error("Idle logoutRedirect failed:", err);
    }
  };

  const { reset: resetIdle } = useIdleTimeout({
    enabled: Boolean(userDisplayName) && isMsalInitialized,
    idleMs: IDLE_TOTAL_MS,
    warningMs: IDLE_WARNING_MS,
    onWarn: () => setIdleWarningOpen(true),
    onExpire: handleIdleExpire,
  });

  // If still initializing, show spinner
  if (!isMsalInitialized) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Initializing...
        </Typography>
      </Container>
    );
  }

  // If tenant is disallowed, show AccessDenied
  if (isTenantDisallowed) {
    return <AccessDenied onTryDifferentAccount={handleTryAnotherAccount} tenantId={tenantId || undefined} />;
  }

  // UPDATED: Render with new provider hierarchy
  return (
    <PopupBlockerDetector 
      onDetectBlocked={handlePopupBlocked}
      checkOnMount={true}
      showWarning={true}
    >  
      {/* NEW: Provider hierarchy with AppCache at the top level */}
      <AppCacheProvider>
        <SubscriptionProviderWithCache>
          <Router>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                background: "linear-gradient(135deg, #f8fafb 0%, #e9ecef 50%, #f1f3f4 100%)", // More sophisticated gradient
                position: 'relative'
              }}
            >
              <AppBar position="static" sx={{ 
                backgroundColor: "#e8e8e8", // Slightly warmer gray
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)', // Subtle bottom border
              }} elevation={2}> {/* Reduced elevation for cleaner look */}
                <Toolbar sx={{ minHeight: '72px' }}> {/* Slightly taller for better proportion */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <img
                      src="/logo-nobackground-5000-1.png"
                      alt="AppTesting Logo"
                      width="140" // Slightly smaller in header
                      style={{ 
                        marginRight: 10,
                        filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.1))' // Subtle shadow for better definition
                      }}
                    />
                  </Box>

                  {userDisplayName ? (
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{
                        flexGrow: 1,
                        fontWeight: 500,
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        textAlign: "center",
                        color: "#333333"
                      }}
                    >
                      {tenantId ? getTenantDisplayText(tenantId) : "Unknown Tenant"}
                    </Typography>
                  ) : (
                    <div style={{ flexGrow: 1 }} />
                  )}

                  {userDisplayName && (
                    <>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mr: 2, 
                          color: "#333333",
                          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          fontWeight: 400,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <span style={{ color: '#555555', marginRight: '4px' }}>
                          Signed in as:
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {userDisplayName}
                        </span>
                      </Typography>
                      <Button
                        variant="outlined"
                        sx={{ 
                          color: "#333333", 
                          borderColor: "#777777",
                          borderRadius: 2,
                          py: 0.5,
                          textTransform: 'none',
                          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#ff6b00',
                            backgroundColor: 'rgba(255, 107, 0, 0.04)',
                            color: '#ff6b00'
                          }
                        }}
                        onClick={handleLogout}
                      >
                        Sign Out
                      </Button>
                    </>
                  )}
                </Toolbar>
              </AppBar>

              <Routes>
                {/* Public Landing Page with Onboarding Info */}
                <Route path="/getting-started" element={<LandingPage />} />

                {/* Marketplace Landing Route */}
                <Route path="/marketplace-landing" element={
                  <Suspense fallback={
                    <Container sx={{ mt: 4, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading marketplace subscription setup...
                      </Typography>
                    </Container>
                  }>
                    <MarketplaceLanding
                      onSubscriptionComplete={handleSubscriptionComplete}
                      onSubscriptionError={handleSubscriptionError}
                    />
                  </Suspense>
                } />          
                
                {/* Add SAML Tester route */}
                <Route path="/samlTester" element={
                  userDisplayName ? (
                    <MainLayoutWrapper 
                      isMsalInitialized={true} 
                      userDisplayName={userDisplayName} 
                    />
                  ) : (
                    <Navigate to="/" replace />
                  )
                } />
                
                {/* Main App Route */}
                <Route path="/*" element={
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {userDisplayName ? (
                      tenantCheckComplete && showOnboarding ? (
                        <TenantManager
                          clientId={clientId}
                          onOnboardingComplete={handleOnboardingComplete}
                          onCancel={handleCancelOnboarding}
                          tenantVerified={isTenantOnboarded} 
                        />
                      ) : (
                        <MainLayoutWrapper 
                          isMsalInitialized={true} 
                          userDisplayName={userDisplayName} 
                        />
                      )
                    ) : (
                      <Container sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
                        <Card sx={{ 
                          p: 4, 
                          maxWidth: 550, 
                          textAlign: "center", 
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)', // Softer, more modern shadow
                          borderRadius: 4, // Increased border radius
                          border: '1px solid rgba(255, 255, 255, 0.2)', // Subtle border
                          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)', // Very subtle gradient
                        }}>
                          <CardContent sx={{ '&:last-child': { pb: 4 } }}>
                            <Box sx={{ mb: 1.5 }}>
                              <img
                                src="/logo-nobackground-5000-1.png"
                                alt="AppTesting Logo"
                                width="320" // Slightly smaller for better proportion
                                className="logo"
                                style={{ 
                                  marginBottom: 16,
                                  filter: 'drop-shadow(0 2px 8px rgba(255, 107, 0, 0.15))' // Subtle logo shadow
                                }}
                              />
                            </Box>
                            
                            {/* <Typography 
                              variant="h5" 
                              sx={{ 
                                mb: 1,
                                fontWeight: 600,
                                color: '#2c3e50',
                                fontFamily: "'Segoe UI', system-ui, sans-serif"
                              }}
                            >
                              Welcome to AppConfig²
                            </Typography> */}
                            
                            {/* Enhanced sign-in button */}
                            <Button
                              variant="contained"
                              onClick={handleLogin}
                              size="large"
                              sx={{
                                mt: 1,
                                width: "100%",
                                py: 1.8,
                                fontSize: "1.1rem",
                                fontFamily: "'Segoe UI', system-ui, sans-serif",
                                fontWeight: 600,
                                letterSpacing: "0.01em",
                                backgroundColor: "#ff6b00",
                                borderRadius: 3,
                                boxShadow: "0 4px 16px rgba(255, 107, 0, 0.3)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  backgroundColor: "#e65c00",
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 8px 24px rgba(255, 107, 0, 0.4)"
                                },
                                "&:active": {
                                  transform: "translateY(0px)",
                                  transition: "all 0.1s"
                                }
                              }}
                              startIcon={<LoginIcon />}
                            >
                              Sign In with Microsoft
                            </Button>

                            {/* First-time user note */}
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                              <Typography 
                                variant="body2" 
                                color="textSecondary"
                                sx={{ display: 'inline', mr: 1 }}
                              >
                                First-time user?
                              </Typography>
                              <MuiLink 
                                href="/getting-started" 
                                target="_blank"
                                sx={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: '#1976d2',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                View setup guide <OpenInNewIcon fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                              </MuiLink>
                            </Box>
                            
                            {/* Optional: Add security/privacy note */}
                            {/* <Typography 
                              variant="caption" 
                              color="textSecondary" 
                              sx={{ 
                                mt: 2, 
                                display: 'block',
                                fontSize: '0.75rem',
                                lineHeight: 1.4
                              }}
                            >
                              Secure authentication powered by Microsoft Entra™ ID
                            </Typography> */}
                          </CardContent>
                        </Card>
                      </Container>
                    )}
                  </Box>
                } />
              </Routes>

              <Footer />
            </div>
          </Router>
          <IdleWarningDialog
            open={idleWarningOpen}
            countdownSeconds={Math.floor(IDLE_WARNING_MS / 1000)}
            onStaySignedIn={() => {
              setIdleWarningOpen(false);
              resetIdle();
            }}
            onSignOutNow={() => {
              setIdleWarningOpen(false);
              handleIdleExpire();
            }}
          />
        </SubscriptionProviderWithCache>
      </AppCacheProvider>
    </PopupBlockerDetector>
  );
};

export default App;